// =============================================================================
// USABLE ABILITY MODAL
// =============================================================================
// [CREATED: 2026-05-30 — Sub-plan 1H-G-bw Stone 2]
// [PURPOSE]: System-agnostic resolution modal for any player-invokable ability
//   that has a roll → optional save → apply-effect lifecycle.
//   The first wired consumer is Lay on Hands (no dice roll; HP input + pool
//   decrement). Future consumers: breathWeapon QB entry (4d6 + DEX save),
//   Hellish Rebuke reactionTrigger, Stunning Strike Ki spend option.
//
// [RULE 16/17]: This modal knows NOTHING about specific class/ability names.
//   It is entirely data-driven by its props. No `title === 'Hellish Rebuke'`
//   or `className === 'Paladin'` checks exist here.
//
// [RULE 19 — useModalEscape]: provided by MobileModal (src/components/MobileModal.js),
//   which registers useModalEscape(isOpen, onClose) on this modal's behalf —
//   migrated onto the MobileModal scaffold under UI-MOBILEMODAL-ADOPTION wave 1
//   (2026-07-04, audit finding B-3: no max-h/overflow-y handling on phones).
//
// [RULE 25 — BREADCRUMB — CW authoring (Stone 8)]:
//   The rollSpec shape surfaced to the modal via props was designed to be
//   authorable through EffectComposer.jsx. Stone 8 (EffectComposer rollSpec
//   accordion) is deferred — see Sub-plan 1H-G-bw-S8 in master-queue.md.
//   Until Stone 8 ships, rollSpec on reactionTrigger/spendActionMenu effects
//   is authored in data files only (srdClassFeatures.js, srdRaceFeatures.js).
//
// [ENDGAME — rollProtocol]: The `rollProtocol` prop is the Daggerheart seam.
//   Today only 'd20' is implemented; 'dualityDice' is stubbed and displays a
//   "coming soon" note in the roll step. When the Daggerheart arc ships, the
//   dualityDice branch in rollProtocol.js is filled in and the modal UI gains
//   a Hope/Fear display row — the prop contract here does not need to change.
//
// PROPS:
//   isOpen        bool            — controls visibility
//   onClose       () => void      — called on cancel or after Apply
//   title         string          — ability name shown in header ("Lay on Hands")
//   rollSpec      object | null   — describes dice rolls + save + effects
//                                   null → LoH-mode (pure HP input, no dice)
//     rollSpec.rolls       [{dice, label}]  e.g. [{dice:'2d10', label:'fire'}]
//     rollSpec.save        {ability, dc, halfOnSave} | null
//     rollSpec.targetMode  'self'|'ally'|'enemy'|'any'|'aoe'|null
//     rollSpec.applyEffects [{type, value, onSaveFail, duration}]
//   costSpend     {type, poolId?, classKey?, slotLevel?} | null
//                 consumed by the CALLER's onResolve (not by this modal)
//   rollProtocol  'd20'|'dualityDice'|'custom'  (default: 'd20')
//   maxPoolHp     number | null   — for LoH: max HP available in pool (shown as input cap)
//   onResolve     (resolution) => void
//                 Called on "Apply". Shape:
//                 {
//                   healAmount:   number | null,  — LoH HP heal amount
//                   cureAction:   bool,            — LoH "cure disease/poison" chosen
//                   rolledTotals: number[],        — one per rollSpec.rolls entry
//                   breakdowns:   string[],        — human-readable rolls
//                   saveOutcome:  'success'|'failure'|null,
//                   target:       string,           — free-text target name ('' = self)
//                   costSpend:    object | null,    — echoed from prop
//                 }

import React, { useState, useCallback } from 'react';
import MobileModal from './MobileModal';
import { executeRollProtocol } from '../utils/rollProtocol';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function deriveTargetLabel(targetMode) {
  switch (targetMode) {
    case 'ally': return 'Healing target (ally name)';
    case 'enemy': return 'Target creature name';
    case 'any': return 'Target name';
    default: return null; // 'self' / null / 'aoe' → skip target input
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function UsableAbilityModal({
  isOpen,
  onClose,
  title = 'Use Ability',
  rollSpec = null,
  costSpend = null,
  rollProtocol = 'd20',
  maxPoolHp = null,
  onResolve,
}) {
  // Escape-key dismissal (Rule 19) now provided by MobileModal's own
  // useModalEscape(isOpen, onClose) registration — do not double-register here.

  // --- local state ---
  const [target, setTarget] = useState('');
  const [healInput, setHealInput] = useState('');
  const [cureAction, setCureAction] = useState(false);
  const [rolled, setRolled] = useState(null);    // result from executeRollProtocol
  const [saveOutcome, setSaveOutcome] = useState(null); // 'success' | 'failure' | null

  // Reset state every time the modal opens
  const resetState = useCallback(() => {
    setTarget('');
    setHealInput('');
    setCureAction(false);
    setRolled(null);
    setSaveOutcome(null);
  }, []);

  // Controlled open/close — reset on open
  const handleClose = useCallback(() => {
    resetState();
    onClose?.();
  }, [resetState, onClose]);

  if (!isOpen) return null;

  // --- derived flags ---
  const isLohMode = rollSpec === null;          // Lay on Hands: no dice, just HP input
  const hasRolls = !isLohMode && Array.isArray(rollSpec?.rolls) && rollSpec.rolls.length > 0;
  const hasSave = !isLohMode && rollSpec?.save != null;
  const targetLabel = deriveTargetLabel(rollSpec?.targetMode);
  const showTargetInput = targetLabel !== null;

  const maxHeal = typeof maxPoolHp === 'number' ? maxPoolHp : Infinity;
  const healAmt = parseInt(healInput, 10);
  const healValid = !isNaN(healAmt) && healAmt > 0 && healAmt <= maxHeal;

  const canApply = (() => {
    if (isLohMode) {
      // Either a heal amount (valid) OR the "cure" checkbox is checked
      return cureAction || healValid;
    }
    // Dice mode: must have rolled (or no rolls to make), and save must be settled if present
    if (hasRolls && !rolled) return false;
    if (hasSave && saveOutcome === null) return false;
    return true;
  })();

  const handleRoll = () => {
    if (!hasRolls) return;
    const result = executeRollProtocol({ rolls: rollSpec.rolls }, rollProtocol);
    setRolled(result);
  };

  const handleApply = () => {
    if (!canApply) return;

    const resolution = {
      // LoH-specific
      healAmount: isLohMode ? (cureAction ? 0 : healAmt) : null,
      cureAction: isLohMode ? cureAction : false,
      // Dice-mode
      rolledTotals: rolled?.totals ?? [],
      breakdowns: rolled?.breakdowns ?? [],
      saveOutcome: hasSave ? saveOutcome : null,
      // Shared
      target,
      costSpend,
    };

    onResolve?.(resolution);
    handleClose();
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <MobileModal
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      showSaveButton={false}
    >
      {/* [UI-MOBILEMODAL-ADOPTION wave 1]: MobileModal's shell now carries
          role="dialog"/aria-modal/aria-label itself (string title auto-labels),
          so the inner wrapper must NOT duplicate role="dialog" — nested dialogs
          break getByRole('dialog') strict-mode locators (e2e
          sub-plan-a2-usable-ability-modal-visual.spec.ts resolves to the shell). */}
      <div className="flex flex-col gap-4">
        {/* ── Step 1: Target (optional) ─────────────────────────────────── */}
        {showTargetInput && (
          <div>
            <label className="block text-xs text-gray-400 mb-1" htmlFor="uam-target">
              {targetLabel}
            </label>
            <input
              id="uam-target"
              type="text"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              placeholder="e.g. Goblin Boss"
              className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-amber-400"
            />
          </div>
        )}

        {/* ── Step 2a: LoH HP input ─────────────────────────────────────── */}
        {isLohMode && (
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-gray-400 mb-1" htmlFor="uam-heal">
                HP to restore
                {typeof maxPoolHp === 'number' && ` (max ${maxPoolHp} from pool)`}
              </label>
              <input
                id="uam-heal"
                type="number"
                min={1}
                max={typeof maxPoolHp === 'number' ? maxPoolHp : undefined}
                value={healInput}
                onChange={(e) => {
                  setCureAction(false);  // mutually exclusive with cure
                  setHealInput(e.target.value);
                }}
                disabled={cureAction}
                placeholder="0"
                className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-amber-400 disabled:opacity-40"
              />
            </div>
            {/* Cure disease / poison option (costs 5 HP from pool per PHB p.84) */}
            {(typeof maxPoolHp !== 'number' || maxPoolHp >= 5) && (
              <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={cureAction}
                  onChange={(e) => {
                    setCureAction(e.target.checked);
                    if (e.target.checked) setHealInput('');
                  }}
                  className="accent-amber-400"
                  data-testid="uam-cure-checkbox"
                />
                Cure disease or poison (costs 5 HP from pool)
              </label>
            )}
          </div>
        )}

        {/* ── Step 2b: Dice roll ────────────────────────────────────────── */}
        {hasRolls && (
          <div className="space-y-2">
            <div className="text-xs text-gray-400">Dice to roll:</div>
            <ul className="space-y-1">
              {rollSpec.rolls.map((r, i) => (
                <li key={i} className="flex items-center justify-between text-sm">
                  <span className="text-gray-300">{r.label}</span>
                  <span className="font-mono text-amber-200">{r.dice}</span>
                </li>
              ))}
            </ul>
            <button
              type="button"
              onClick={handleRoll}
              disabled={!!rolled && rollProtocol !== 'dualityDice'}
              className="w-full py-2 px-4 bg-purple-700 hover:bg-purple-600 disabled:bg-gray-700 disabled:text-gray-500 text-white text-sm font-semibold rounded transition-colors"
            >
              {rolled ? 'Re-roll' : 'Roll Dice'}
            </button>
            {/* dualityDice stub notice */}
            {rollProtocol === 'dualityDice' && (
              <p className="text-[11px] text-amber-500 text-center">
                Daggerheart duality dice — coming in a future update.
              </p>
            )}
            {/* Roll result */}
            {rolled && !rolled.stub && (
              <div className="bg-gray-800/60 border border-gray-700 rounded p-2 space-y-1">
                {rolled.totals.map((total, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span className="text-gray-400">{rolled.labels?.[i] || rollSpec.rolls[i]?.label}</span>
                    <span className="font-bold text-amber-200">
                      {total} <span className="font-normal text-gray-500 text-xs">({rolled.breakdowns?.[i]})</span>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Step 3: Saving throw (self-report per Fork 3 spec decision) ─ */}
        {hasSave && (
          <div className="space-y-2">
            <div className="text-xs text-gray-300">
              Target makes a{' '}
              <span className="font-semibold uppercase text-amber-200">{rollSpec.save.ability}</span>{' '}
              save
              {rollSpec.save.dc != null && (
                <> · DC <span className="font-bold text-amber-300">{rollSpec.save.dc}</span></>
              )}
              {rollSpec.save.halfOnSave && (
                <span className="ml-1 text-gray-500 text-[10px]">(half on success)</span>
              )}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setSaveOutcome('success')}
                className={`flex-1 py-1.5 text-sm font-semibold rounded border transition-colors ${
                  saveOutcome === 'success'
                    ? 'bg-green-700 border-green-500 text-white'
                    : 'bg-gray-800 border-gray-600 text-gray-300 hover:border-green-500'
                }`}
              >
                Success
              </button>
              <button
                type="button"
                onClick={() => setSaveOutcome('failure')}
                className={`flex-1 py-1.5 text-sm font-semibold rounded border transition-colors ${
                  saveOutcome === 'failure'
                    ? 'bg-red-700 border-red-500 text-white'
                    : 'bg-gray-800 border-gray-600 text-gray-300 hover:border-red-500'
                }`}
              >
                Failure
              </button>
            </div>
          </div>
        )}

        {/* ── Footer: Apply + Cancel ────────────────────────────────────── */}
        <div className="flex gap-2 pt-1">
          <button
            type="button"
            onClick={handleClose}
            className="flex-1 py-2 text-sm text-gray-300 bg-gray-800 hover:bg-gray-700 rounded border border-gray-600 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleApply}
            disabled={!canApply}
            className="flex-1 py-2 text-sm font-bold text-white bg-amber-600 hover:bg-amber-500 disabled:bg-gray-700 disabled:text-gray-500 rounded transition-colors"
          >
            Apply
          </button>
        </div>
      </div>
    </MobileModal>
  );
}
